import type { OrganizationIdInput } from '@/lib/organization-scoping'
import { readProperty } from '@/lib/utils'

const sharedRunLifetimeMs = 10 * 60 * 1000

/** What a browser remembers about a print request so a retry finds the same Snapdragon job. */
export interface LocalPrintIntent {
  clientJobId: string
  jobId: string
}

/** The fields a coordination failure carries, as `LocalPrintError` accepts them. */
export interface LocalPrintCoordinationErrorDetails {
  code: string
  jobId?: string
  outcomeUnknown?: boolean
}

/** A failed run, rebuilt from browser storage so another tab can rethrow it. */
export type RestoredLocalPrintError = Error & {
  code: string
  httpStatus: number | null
  jobId: string
  jobStatus: string
  outcomeUnknown: boolean
  intentOrganizationId: string
  intentKey: string
}

type IntentRecord = Record<string, unknown>

type RecentOutcome =
  | { status: 'fulfilled'; value: unknown }
  | { status: 'rejected'; error: RestoredLocalPrintError }

interface LocalPrintIntentRun {
  organizationId: OrganizationIdInput
  intentKey: string
  execute: () => Promise<unknown>
}

export interface LocalPrintIntentCoordinator {
  clearIntent: (organizationId: OrganizationIdInput, intentKey: string) => boolean
  loadIntent: (organizationId: OrganizationIdInput, intentKey: string) => LocalPrintIntent | null
  retainIntent: (
    organizationId: OrganizationIdInput,
    intentKey: string,
    intent: LocalPrintIntent
  ) => boolean
  /** Resolves to this run's result, or to the stored JSON result of another tab's run. */
  run: (run: LocalPrintIntentRun) => Promise<unknown>
}

function sharedNow() {
  const performance = globalThis.performance
  return typeof performance?.now === 'function' && Number.isFinite(performance.timeOrigin)
    ? performance.timeOrigin + performance.now()
    : Date.now()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseObject(value: string | null): IntentRecord {
  try {
    const parsed: unknown = JSON.parse(value || '{}')
    return isRecord(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

// Stored times are numbers; anything else reads as unset.
function storedTime(value: unknown): number {
  return typeof value === 'number' ? value : 0
}

function storedString(value: unknown, key: string, fallback = ''): string {
  const field = readProperty(value, key)
  return typeof field === 'string' && field ? field : fallback
}

// The desktop app can send job ids as numbers, and the JavaScript version stored them as sent.
function storedJobId(value: unknown, key: string): string {
  const field = readProperty(value, key)
  return typeof field === 'number' ? String(field) : storedString(value, key)
}

// Intents are JSON read back from browser storage, so each field is checked.
function storedIntent(value: unknown): LocalPrintIntent | null {
  if (!isRecord(value)) return null

  return {
    clientJobId: storedString(value, 'clientJobId'),
    jobId: storedJobId(value, 'jobId'),
  }
}

function serializableValue(value: unknown): unknown {
  try {
    const serialized: unknown = JSON.parse(JSON.stringify(value ?? null))
    return serialized
  } catch {
    return null
  }
}

function serializedError(error: unknown) {
  const httpStatus = readProperty(error, 'httpStatus')

  return {
    name: storedString(error, 'name', 'Error'),
    message: storedString(error, 'message', 'Direct Printing failed.'),
    code: storedString(error, 'code'),
    httpStatus: typeof httpStatus === 'number' ? httpStatus : null,
    jobId: storedJobId(error, 'jobId'),
    jobStatus: storedString(error, 'jobStatus'),
    outcomeUnknown: readProperty(error, 'outcomeUnknown') === true,
    intentOrganizationId: storedString(error, 'intentOrganizationId'),
    intentKey: storedString(error, 'intentKey'),
  }
}

function restoredError(stored: unknown): RestoredLocalPrintError {
  const { message, ...fields } = serializedError(stored)
  return Object.assign(new Error(message), fields)
}

export function createLocalPrintIntentCoordinator({
  pendingStorageKey,
  createCoordinationError = (message, details) => Object.assign(new Error(message), details),
}: {
  pendingStorageKey: (organizationId: OrganizationIdInput) => string
  createCoordinationError?: (message: string, details: LocalPrintCoordinationErrorDetails) => Error
}): LocalPrintIntentCoordinator {
  const activeRuns = new Map<string, Promise<unknown>>()

  const recordKey = (organizationId: OrganizationIdInput, intentKey: string) =>
    `${pendingStorageKey(organizationId)}.intent.${encodeURIComponent(intentKey)}`

  function sweepExpiredRecords(organizationId: OrganizationIdInput) {
    if (typeof window === 'undefined') return

    const prefix = `${pendingStorageKey(organizationId)}.intent.`
    const storage = window.localStorage
    const keys = []
    let examinedRecords = 0

    for (let index = 0; index < storage.length && examinedRecords < 100; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(prefix)) {
        keys.push(key)
        examinedRecords += 1
      }
    }

    const now = Date.now()
    for (const key of keys) {
      const record = parseObject(storage.getItem(key))
      const expiresAt = storedTime(record.expiresAt)
      if (expiresAt && expiresAt < now) {
        if (isRecord(record.intent)) {
          storage.setItem(key, JSON.stringify({ version: 2, intent: record.intent }))
        } else {
          storage.removeItem(key)
        }
      }
    }
  }

  function loadRecord(organizationId: OrganizationIdInput, intentKey: string): IntentRecord {
    if (typeof window === 'undefined') return {}
    return parseObject(window.localStorage.getItem(recordKey(organizationId, intentKey)))
  }

  function loadLegacyIntent(organizationId: OrganizationIdInput, intentKey: string) {
    if (typeof window === 'undefined') return null
    const intents = parseObject(window.localStorage.getItem(pendingStorageKey(organizationId)))
    return storedIntent(intents[intentKey])
  }

  function loadIntent(organizationId: OrganizationIdInput, intentKey: string) {
    const intent = storedIntent(loadRecord(organizationId, intentKey).intent)
    if (intent) return intent
    return loadLegacyIntent(organizationId, intentKey)
  }

  function writeRecord(
    organizationId: OrganizationIdInput,
    intentKey: string,
    record: IntentRecord
  ) {
    if (typeof window === 'undefined') return false
    try {
      window.localStorage.setItem(
        recordKey(organizationId, intentKey),
        JSON.stringify({ version: 2, ...record })
      )
      return true
    } catch {
      return false
    }
  }

  function retainIntent(
    organizationId: OrganizationIdInput,
    intentKey: string,
    intent: LocalPrintIntent
  ) {
    return writeRecord(organizationId, intentKey, { intent })
  }

  function clearLegacyIntent(organizationId: OrganizationIdInput, intentKey: string) {
    const key = pendingStorageKey(organizationId)
    const intents = parseObject(window.localStorage.getItem(key))
    if (!Object.hasOwn(intents, intentKey)) return true

    delete intents[intentKey]
    try {
      window.localStorage.setItem(key, JSON.stringify(intents))
      return true
    } catch {
      return false
    }
  }

  function clearIntent(organizationId: OrganizationIdInput, intentKey: string) {
    if (typeof window === 'undefined') return false
    try {
      if (typeof window.localStorage.removeItem === 'function') {
        window.localStorage.removeItem(recordKey(organizationId, intentKey))
      } else {
        window.localStorage.setItem(recordKey(organizationId, intentKey), '')
      }
      return clearLegacyIntent(organizationId, intentKey)
    } catch {
      return false
    }
  }

  function recentOutcome(
    organizationId: OrganizationIdInput,
    intentKey: string,
    startedAt: number
  ): RecentOutcome | null {
    const record = loadRecord(organizationId, intentKey)
    const completedAt = storedTime(record.completedAt)
    const expiresAt = storedTime(record.expiresAt)
    if (!completedAt || completedAt < startedAt) return null
    if (expiresAt && expiresAt < Date.now()) return null

    return record.outcome === 'fulfilled'
      ? { status: 'fulfilled', value: record.value }
      : { status: 'rejected', error: restoredError(record.error) }
  }

  function retainOutcome(
    organizationId: OrganizationIdInput,
    intentKey: string,
    outcome: IntentRecord,
    pendingIntent: LocalPrintIntent | null = null
  ) {
    const completedAt = sharedNow()
    return writeRecord(organizationId, intentKey, {
      ...(pendingIntent ? { intent: pendingIntent } : {}),
      completedAt,
      expiresAt: completedAt + sharedRunLifetimeMs,
      ...outcome,
    })
  }

  async function executeWithinLock({
    organizationId,
    intentKey,
    startedAt,
    execute,
  }: LocalPrintIntentRun & { startedAt: number }): Promise<unknown> {
    const prior = recentOutcome(organizationId, intentKey, startedAt)
    if (prior?.status === 'fulfilled') return prior.value
    if (prior?.status === 'rejected') throw prior.error

    try {
      const result = await execute()
      if (
        !retainOutcome(organizationId, intentKey, {
          outcome: 'fulfilled',
          value: serializableValue(result),
        })
      ) {
        throw createCoordinationError(
          'Snapdragon accepted this job, but the browser could not safely coordinate another tab. Check the printer before printing another copy.',
          {
            code: 'job_completion_storage_failed',
            jobId: storedJobId(result, 'id'),
            outcomeUnknown: true,
          }
        )
      }
      return result
    } catch (error) {
      const pendingIntent =
        readProperty(error, 'outcomeUnknown') === true
          ? loadIntent(organizationId, intentKey)
          : null
      retainOutcome(
        organizationId,
        intentKey,
        { outcome: 'rejected', error: serializedError(error) },
        pendingIntent
      )
      throw error
    }
  }

  function run({ organizationId, intentKey, execute }: LocalPrintIntentRun) {
    sweepExpiredRecords(organizationId)
    const activeKey = `${String(organizationId)}\n${intentKey}`
    const activeRun = activeRuns.get(activeKey)
    if (activeRun) return activeRun

    const startedAt = sharedNow()
    const executeRun = async () => {
      const locks = globalThis.navigator?.locks
      if (typeof locks?.request !== 'function') {
        throw createCoordinationError(
          'Direct Printing needs a current browser version before it can safely print.',
          { code: 'browser_coordination_unavailable' }
        )
      }
      return locks.request(
        `snapdragon:local-print-intent:${recordKey(organizationId, intentKey)}`,
        () => executeWithinLock({ organizationId, intentKey, startedAt, execute })
      )
    }
    const runPromise: Promise<unknown> = executeRun().finally(() => {
      if (activeRuns.get(activeKey) === runPromise) activeRuns.delete(activeKey)
    })
    activeRuns.set(activeKey, runPromise)
    return runPromise
  }

  return {
    clearIntent,
    loadIntent,
    retainIntent,
    run,
  }
}
